import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridComponent } from '@progress/kendo-angular-grid';
import { environment } from '../../../../../../environments/environment';
import { CalculationModelTemplate } from '../../../models/calculation-model-template';
import { CalculationModelService } from '../../../services/calculation-model-service';
import { process } from "@progress/kendo-data-query";
import { ImportPreviewRenderService } from '../../../../../components/common/services/import-preview-render-service';
import { CalculationModelBase } from '../calculation-model-base';
import { UploadComponent } from '@progress/kendo-angular-upload';
import { AppServiceHelperService } from '../../../../../services/app-service-helper.service';
import { FileDownloadService } from '../../../../../services/file-download-service.service';
import { EventBusService } from '../../../../../services/event-bus.service';
import { AppLoadingIndicatorService } from '../../../../../services/app-loading-indicator.service';

/**
 * Calculation model (import template) registration component
 */
@Component({
  selector: 'calculation-model-register',
  templateUrl: './calculation-model-register.component.html',
  styleUrls: ['./calculation-model-register.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CalculationModelRegisterComponent extends CalculationModelBase implements OnInit {
  /**
   * Close pane event listener
   */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
   * Close with refresh evet listener
   */
  @Output() closeWithRefresh: EventEmitter<any> = new EventEmitter();

  /**
   * The Kendo upload component for uploading the file
   */
  @ViewChild("kendoUpload") kendoUpload: UploadComponent;

  /**
   * The Kendo grid component for template editing
   */
  @ViewChild("templateEditGrid") templateEditGrid: GridComponent;

  /**
   * 
   * @param cmService CalculationModelService
   * @param elRef ElementRef
   * @param iprs ImportPreviewRenderService
   */
  constructor(cmService: CalculationModelService,
    eventBus: EventBusService,
    elRef: ElementRef,
    public iprs: ImportPreviewRenderService,
    private appServiceHelper: AppServiceHelperService,
    private fileDownloadService: FileDownloadService,
    protected loaderService: AppLoadingIndicatorService) {
    super(cmService, eventBus, elRef, loaderService);

    this.stepsCount = 3;
    this.templateDto = {
      oid: 0,
      name: '',
      countryId: 0,
      sectorId: 0,
      valuationMethodId: 0,
      statusId: 0,
      mappingVersionId: environment.leadMappingVersionId,
      originalFilename: ''
    };
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.loadReflists().then(() => {
      this.templatesList.push(this.templateDto);
      this.gridData = process(this.templatesList, this.gridState);

      //set grid (for template info) in edit mode
      setTimeout(() => {
        this.templateEditGrid.editRow(0);
      }, 100);
    });
  }

  /**
   * Close click event handler
   */
  onCloseClicked(): void {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);//let animation finish
  }

  /**
   * Go back event handler
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  /**
   * Next button enabled?
   */
  isNextEnabled(): boolean {
    switch (this.currentStep) {
      case 1: {
        return this.importFiles.length === 1;
      }
      case 2: {
        return this.uploadedFiles.length === 1;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * Goes to the next step
   */
  async nextStep(): Promise<void> {
    if (this.currentStep >= this.stepsCount) {
      return;
    }

    let success: boolean;

    switch (this.currentStep) {
      case 1:
        success = await this.uploadTemplate();
        this.currentStep += (success ? 1 : 0);
        break;
      case 2:
        if (!this.previewData) {
          success = await this.startPreuploadedImportPreview();
          this.currentStep += (success ? 1 : 0);
        } else {
          //preview already done
          this.currentStep += 1;
        }
        break;
      case 3:
        //nothing to check here, user can prepare mapping or not
        this.currentStep += 1;
        break;
      default:
        break;
    }
  }

  /**
   * File remove event handler
   * @param upload UploadComponent
   * @param uid UID set by the UploadComponent
   */
  onRemoveFile(upload: UploadComponent, uid: string): void {
    upload.removeFilesByUid(uid);
    this.importFiles = [];
    this.uploadedFiles = [];
    this.previewData = null;
  }

  /**
   * Upload step
   */
  async uploadTemplate(): Promise<boolean> {
    if (this.importFiles.length !== 1) {
      return false;//no file for upload
    }

    this.showLoader(true);
    return new Promise((resolve, _reject) => {
      this.cmService.uploadCalculationModelTemplate({ isTemplate: true, importFiles: this.importFiles }).subscribe({
        next: (result: any) => {
          this.uploadedFiles = result.data.map(
            (entry) => ({
              ...entry,
              isSelected: true,
              groupDescription: this.importGroups.find(g => g.type === entry.groupId).value,
              mappingVersionDescription: this.newestTemplateMappingVersion.description
            })
          );

          this.showLoader(false);
          resolve(true);//will switch to step 2
        },
        error: (_reason) => {
          this.showLoader(false);
          alert('Failed to upload template!');
          resolve(false);
        }
      });
    });
  }

  /**
   * Prepare mapping step
   * @param item File entry
   */
  async prepareMapping(item): Promise<boolean> {
    if (item.groupId !== 5) return false;

    this.showLoader(true);
    return new Promise((resolve, _reject) => {
      this.cmService.prepareMapping(item.fileImportGuid).subscribe({
        next: (_result: any) => {
          this.showLoader(false);
          resolve(true);
        },
        error: (_reason) => {
          this.showLoader(false);
          alert('Failed to prepare mapping!');
          resolve(false);
        }
      });
    });
  }

  /**
   * Save template
   */
  saveTemplate(): void {
    const calcModel = this.gridData.data[0] as CalculationModelTemplate;
    if (!calcModel.name) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'templateName');
      return;
    }

    calcModel.originalFilename = this.uploadedFiles[0].fileImportGuid;

    this.showLoader(true);
    this.cmService.createCalculationModel(calcModel).subscribe({
      next: (saved: any) => {
        this.showLoader(false);
        if (saved && saved.oid > 0) {
          //all went ok, close pane
          this.shouldClose = true;
          setTimeout(() => {
            this.closeWithRefresh.emit();
          }, 500);//let animation finish
        } else {
          alert('Failed to save template!');
        }
      },
      error: (_reason) => {
        this.showLoader(false);
        alert('Failed to save template!');
      }
    });
  }

  /**
   * Download the file
   * @param item File entry
   */
  downloadFileItem(item) {
    const url = `import/v2/import/downloadfile/${item.fileImportGuid}`;
    const downloadUrl = this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', url);
    this.fileDownloadService.initiateDownload(downloadUrl, null, false);
  }
}
